/**
 * Diff tree renderer.
 *
 * Converts a TDiffNode tree into an HTML string with <ins>/<del> markup
 * and diff-ins/diff-del classes for change tracking.
 */
import { stableValue } from '../core/compare';
import { serializeElement, renderMathFormulaCached, CHANGES_MATH_NODE_NAME } from '../parser/parser';
import { TABLE_CELL_TAGS, ROOT_TAG, type DiffMark } from '../schema';
import type { TDiffNode } from '../types';
import { diffRenderedMath } from './mathDiff';
import {
  escapeHtml,
  renderAttribute,
  replaceInnerHtml,
  CHEERIO_REQUIRED_TAGS,
  buildElementHtml,
  addClass,
} from './html';

// Re-export for backward compat - external consumers import escapeHtml from here
export { escapeHtml } from './html';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type RenderSpecialNodeHelpers = {
  renderChildren: () => Promise<string>;
  renderDefault: () => Promise<string>;
  wrapInserted: (html: string) => string;
  wrapDeleted: (html: string) => string;
  wrapCurrentMarks: (html: string) => string;
  wrapPreviousMarks: (html: string) => string;
};

export type RenderDiffTreeOptions = {
  renderSpecialNode?: (node: TDiffNode, helpers: RenderSpecialNodeHelpers) => Promise<string | null>;
  mathCache?: Map<string, string>;
};

// ---------------------------------------------------------------------------
// Mark wrapping
// ---------------------------------------------------------------------------

const wrapMarksHtml = (html: string, marks: readonly DiffMark[]): string =>
  [...marks].reverse().reduce((content, mark) => {
    switch (mark.name) {
      case 'bold':
        return `<strong>${content}</strong>`;
      case 'strike':
        return `<s>${content}</s>`;
      case 'italic':
        return `<em>${content}</em>`;
      case 'underline':
        return `<u>${content}</u>`;
      case 'code':
        return `<code>${content}</code>`;
      case 'superscript':
        return `<sup>${content}</sup>`;
      case 'subscript':
        return `<sub>${content}</sub>`;
      case 'link': {
        // Skip link wrapping if content already contains an <a> tag to prevent
        // invalid nested anchors (e.g. a link-marked ref that renders as <a href="#...">).
        if (/<a\s/i.test(content)) return content;
        const href = typeof mark.attrs.href === 'string' ? mark.attrs.href : '';
        const target = typeof mark.attrs.target === 'string' ? ` target="${escapeHtml(mark.attrs.target)}"` : '';
        const rel = typeof mark.attrs.rel === 'string' ? ` rel="${escapeHtml(mark.attrs.rel)}"` : '';
        return `<a href="${escapeHtml(href)}"${target}${rel}>${content}</a>`;
      }
      case 'customStyleMark': {
        const customStyle = typeof mark.attrs.customStyleKey === 'string' ? mark.attrs.customStyleKey : '';
        const attr = customStyle ? ` data-custom-style="${escapeHtml(customStyle)}"` : '';
        return `<span${attr}>${content}</span>`;
      }
      case 'highlight': {
        const color = typeof mark.attrs.color === 'string' ? mark.attrs.color : '';
        const className = typeof mark.attrs.class === 'string' ? mark.attrs.class : '';
        const attrs = `${renderAttribute('class', className)}${renderAttribute('data-color', color)}${
          color ? renderAttribute('style', `background-color: ${color}; color: inherit`) : ''
        }`;
        return `<mark${attrs}>${content}</mark>`;
      }
      default:
        return content;
    }
  }, html);

// ---------------------------------------------------------------------------
// Diff wrapping
// ---------------------------------------------------------------------------

const GEOMETRY_ATTR_LABELS: Readonly<Record<string, string>> = {
  rowspan: 'cells merged or split',
  colspan: 'cells merged or split',
};

const GEOMETRY_ATTRS = ['rowspan', 'colspan'] as const;

/** Collects summary badges for a full-table diff (one badge per change type). */
const collectTableGeometryBadges = (node: TDiffNode): string => {
  const changed = new Set<string>();

  const walk = (n: TDiffNode) => {
    if (TABLE_CELL_TAGS.has(n.type) && n.prevAttrs) {
      for (const key of GEOMETRY_ATTRS) {
        if (stableValue(n.prevAttrs[key]) !== stableValue(n.attrs[key])) {
          changed.add(key);
        }
      }
    }
    for (const child of n.children ?? []) walk(child);
  };
  walk(node);

  if (changed.size === 0) return '';
  const labels = new Set([...changed].map((key) => GEOMETRY_ATTR_LABELS[key] ?? key));
  const badges = [...labels].map((label) => `<span class="diff-attr">&#x394; ${escapeHtml(label)}</span>`).join('');
  return `<span class="diff-attr-group">${badges}</span>`;
};

/**
 * Block-level nodes use class-based diff marking (diff-ins/diff-del) because
 * <ins>/<del> are inline elements and cannot properly wrap block content.
 * caption-node is an exception - it renders as <span>, so inline wrapping works,
 * and CSS relies on `ins:has(.caption-wrapper)` for display:block.
 */
const shouldClassWrapNode = (node: TDiffNode): boolean => {
  if (node.isInline || node.isAtom) return false;
  if (node.type === 'caption-node') return false;
  return true;
};

const wrapInsertedNode = (html: string, node: TDiffNode): string =>
  shouldClassWrapNode(node) ? addClass(html, 'diff-ins') : `<ins>${html}</ins>`;

const wrapDeletedNode = (html: string, node: TDiffNode): string =>
  shouldClassWrapNode(node) ? addClass(html, 'diff-del') : `<del>${html}</del>`;

const hasTableGeometryChange = (node: TDiffNode): boolean => {
  if (TABLE_CELL_TAGS.has(node.type) && node.prevAttrs) {
    for (const key of GEOMETRY_ATTRS) {
      if (stableValue(node.prevAttrs[key]) !== stableValue(node.attrs[key])) return true;
    }
  }
  return (node.children ?? []).some(hasTableGeometryChange);
};

const getSourceScopedNode = (node: TDiffNode, source: 'current' | 'previous'): TDiffNode | null => {
  // Text nodes don't carry element references - determine existence from change kind.
  if (node.isText) {
    if (source === 'current' && node.change === 'deleted') return null;
    if (source === 'previous' && node.change === 'inserted') return null;
    if (node.change === 'unchanged') return node;
    return { ...node, change: source === 'current' ? 'inserted' : 'deleted' };
  }
  if (source === 'current' && !node.currElement) return null;
  if (source === 'previous' && !node.prevElement) return null;
  if (node.change === 'unchanged') return node;
  return { ...node, change: source === 'current' ? 'inserted' : 'deleted' };
};

// ---------------------------------------------------------------------------
// Serialization
// ---------------------------------------------------------------------------

const serializeNodeHtml = (
  node: TDiffNode,
  source: 'current' | 'previous',
  options?: RenderDiffTreeOptions
): string => {
  const el = source === 'current' ? node.currElement : node.prevElement;
  if (!el) return '';

  if (node.type === CHANGES_MATH_NODE_NAME) {
    const formula = typeof el.attribs?.['data-formula'] === 'string' ? el.attribs['data-formula'] : '';
    return renderMathFormulaCached(formula, options?.mathCache);
  }

  return serializeElement(el);
};

// ---------------------------------------------------------------------------
// Render functions
// ---------------------------------------------------------------------------
//
// Render dispatch:
//   renderNode → (specialNode || renderDefaultNode) - two-sided diff with ins/del
//   renderOneSided → (specialNode || renderOneSidedDefault) - one side only (inserted/deleted subtrees)

const renderOneSidedDefault = async (
  node: TDiffNode,
  options: RenderDiffTreeOptions,
  source: 'current' | 'previous'
): Promise<string> => {
  if (node.isText) {
    return (node.textSegments ?? [])
      .map((segment) => {
        if (source === 'current' && segment.change === 'deleted') return '';
        if (source === 'previous' && segment.change === 'inserted') return '';
        const marks = source === 'previous' ? (segment.prevMarks ?? segment.marks) : segment.marks;
        return wrapMarksHtml(escapeHtml(segment.text), marks);
      })
      .join('');
  }

  if (node.isAtom) {
    const html = serializeNodeHtml(node, source, options);
    const marks = source === 'previous' ? (node.prevMarks ?? node.marks) : node.marks;
    return node.isInline ? wrapMarksHtml(html, marks) : html;
  }

  const childrenHtml = (
    await Promise.all((node.children ?? []).map((child) => renderOneSided(child, options, source)))
  ).join('');

  if (node.type === ROOT_TAG) return childrenHtml;

  const containerEl = source === 'current' ? node.currElement : node.prevElement;
  if (!containerEl) return childrenHtml;

  if (CHEERIO_REQUIRED_TAGS.has(containerEl.tagName)) {
    return replaceInnerHtml(serializeNodeHtml(node, source, options), childrenHtml);
  }
  return buildElementHtml(containerEl, childrenHtml);
};

const renderOneSided = async (
  node: TDiffNode,
  options: RenderDiffTreeOptions,
  source: 'current' | 'previous'
): Promise<string> => {
  const scopedNode = getSourceScopedNode(node, source);
  if (!scopedNode) return '';

  if (options.renderSpecialNode) {
    const renderChildren = async () =>
      (await Promise.all((scopedNode.children ?? []).map((child) => renderOneSided(child, options, source)))).join('');

    const specialHtml = await options.renderSpecialNode(scopedNode, {
      renderChildren,
      renderDefault: () => renderOneSidedDefault(scopedNode, options, source),
      wrapInserted: (html) => html,
      wrapDeleted: (html) => html,
      wrapCurrentMarks: (html) => (scopedNode.isInline ? wrapMarksHtml(html, scopedNode.marks) : html),
      wrapPreviousMarks: (html) =>
        scopedNode.isInline ? wrapMarksHtml(html, scopedNode.prevMarks ?? scopedNode.marks) : html,
    });

    if (specialHtml != null) return specialHtml;
  }

  return renderOneSidedDefault(scopedNode, options, source);
};

const renderDefaultNode = async (node: TDiffNode, options: RenderDiffTreeOptions): Promise<string> => {
  // Section badge - lightweight marker for section added/removed
  if (node.type === 'section-badge') {
    const label = typeof node.attrs.label === 'string' ? node.attrs.label : '';
    return `<span class="diff-attr-group"><span class="diff-attr">&#x394; ${escapeHtml(label)}</span></span>`;
  }

  // Table with geometry change - render both versions side by side
  if (node.type === 'table' && node.change === 'modified' && hasTableGeometryChange(node)) {
    const badges = collectTableGeometryBadges(node);
    const previousTableHtml = node.prevElement ? await renderOneSided(node, options, 'previous') : '';
    const currentTableHtml = node.currElement ? await renderOneSided(node, options, 'current') : '';
    return `${badges}${previousTableHtml ? wrapDeletedNode(previousTableHtml, node) : ''}${
      currentTableHtml ? wrapInsertedNode(currentTableHtml, node) : ''
    }`;
  }

  // Text nodes
  if (node.isText) {
    return (node.textSegments ?? [])
      .map((segment) => {
        if (segment.change === 'unchanged') {
          return wrapMarksHtml(escapeHtml(segment.text), segment.marks);
        }
        if (segment.change === 'inserted') {
          return `<ins>${wrapMarksHtml(escapeHtml(segment.text), segment.marks)}</ins>`;
        }
        if (segment.change === 'deleted') {
          return `<del>${wrapMarksHtml(escapeHtml(segment.text), segment.prevMarks ?? segment.marks)}</del>`;
        }
        // Format change - show old marks deleted, new marks inserted
        const oldContent = wrapMarksHtml(escapeHtml(segment.text), segment.prevMarks ?? segment.marks);
        const newContent = wrapMarksHtml(escapeHtml(segment.text), segment.marks);
        return `<del>${oldContent}</del><ins>${newContent}</ins>`;
      })
      .join('');
  }

  const childrenHtml = (await Promise.all((node.children ?? []).map((child) => renderNode(child, options)))).join('');

  // Atom nodes
  if (node.isAtom) {
    const currentHtml = serializeNodeHtml(node, 'current', options);
    const previousHtml = serializeNodeHtml(node, 'previous', options);
    const markedCurrent = currentHtml && node.isInline ? wrapMarksHtml(currentHtml, node.marks) : currentHtml;
    const markedPrevious =
      previousHtml && node.isInline ? wrapMarksHtml(previousHtml, node.prevMarks ?? node.marks) : previousHtml;

    if (node.change === 'inserted') return wrapInsertedNode(markedCurrent, node);
    if (node.change === 'deleted') return wrapDeletedNode(markedPrevious, node);
    if (node.change === 'modified') {
      // Character-level math diff: when both formulas exist and marks haven't
      // changed, try a parallel DOM walk on the rendered KaTeX HTML.
      if (
        node.type === CHANGES_MATH_NODE_NAME &&
        markedPrevious &&
        markedCurrent &&
        !node.prevMarks // marks unchanged - prevMarks is only set when marks differ
      ) {
        const prevFormula = node.prevAttrs?.['data-formula'];
        const currFormula = node.attrs['data-formula'];
        if (typeof prevFormula === 'string' && typeof currFormula === 'string') {
          const annotated = diffRenderedMath(prevFormula, currFormula, options.mathCache);
          if (annotated) {
            return node.isInline ? wrapMarksHtml(annotated, node.marks) : annotated;
          }
        }
      }

      if (markedPrevious && markedCurrent) {
        return `${wrapDeletedNode(markedPrevious, node)}${wrapInsertedNode(markedCurrent, node)}`;
      }
      return markedCurrent
        ? wrapInsertedNode(markedCurrent, node)
        : markedPrevious
          ? wrapDeletedNode(markedPrevious, node)
          : '';
    }
    return markedCurrent || markedPrevious;
  }

  // Container nodes
  if (node.type === ROOT_TAG) return childrenHtml;

  // Entirely inserted/deleted containers use renderOneSided (one-sided, no diff markup)
  // to avoid the Cheerio re-parse in replaceInnerHtml below, which is expensive for tables.
  if (node.change === 'inserted') return wrapInsertedNode(await renderOneSided(node, options, 'current'), node);
  if (node.change === 'deleted') return wrapDeletedNode(await renderOneSided(node, options, 'previous'), node);

  // Type change - render both versions side-by-side.
  // th↔td excluded: content is diffed normally, badge indicates the type change.
  const isCellTypeChange =
    !!node.currElement &&
    !!node.prevElement &&
    node.currElement.tagName !== node.prevElement.tagName &&
    TABLE_CELL_TAGS.has(node.currElement.tagName) &&
    TABLE_CELL_TAGS.has(node.prevElement.tagName);
  const typeChanged =
    !!node.currElement &&
    !!node.prevElement &&
    node.currElement.tagName !== node.prevElement.tagName &&
    !isCellTypeChange;
  if (typeChanged) {
    const previousHtml = await renderOneSided(node, options, 'previous');
    const currentHtml = await renderOneSided(node, options, 'current');
    return `${previousHtml ? wrapDeletedNode(previousHtml, node) : ''}${
      currentHtml ? wrapInsertedNode(currentHtml, node) : ''
    }`;
  }

  // Custom style change on paragraph - render both versions side-by-side
  const customStyleChanged =
    node.prevAttrs &&
    node.type === 'p' &&
    (node.prevAttrs['data-custom-style'] ?? '') !== (node.attrs['data-custom-style'] ?? '');
  if (customStyleChanged) {
    const previousHtml = await renderOneSided(node, options, 'previous');
    const currentHtml = await renderOneSided(node, options, 'current');
    return `${previousHtml ? wrapDeletedNode(previousHtml, node) : ''}${
      currentHtml ? wrapInsertedNode(currentHtml, node) : ''
    }`;
  }

  const cellTypeBadge = isCellTypeChange
    ? `<span class="diff-attr-group"><span class="diff-attr">${escapeHtml(
        node.currElement!.tagName === 'th' ? 'cell \u2192 header' : 'header \u2192 cell'
      )}</span></span>`
    : '';

  // Checkbox state change - render both versions side-by-side (old crossed out, new highlighted)
  const isTaskItemCheckedChange =
    node.prevAttrs &&
    node.type === 'li' &&
    node.attrs['data-type'] === 'taskItem' &&
    (node.prevAttrs['data-checked'] ?? '') !== (node.attrs['data-checked'] ?? '');
  if (isTaskItemCheckedChange) {
    const previousHtml = await renderOneSided(node, options, 'previous');
    const currentHtml = await renderOneSided(node, options, 'current');
    return `${previousHtml ? wrapDeletedNode(previousHtml, node) : ''}${
      currentHtml ? wrapInsertedNode(currentHtml, node) : ''
    }`;
  }

  const innerContent = `${cellTypeBadge}${childrenHtml}`;

  const containerEl = node.currElement ?? node.prevElement;
  if (!containerEl) return innerContent;
  const html = CHEERIO_REQUIRED_TAGS.has(containerEl.tagName)
    ? replaceInnerHtml(serializeNodeHtml(node, node.currElement ? 'current' : 'previous', options), innerContent)
    : buildElementHtml(containerEl, innerContent);

  return html;
};

export const renderNode = async (node: TDiffNode, options: RenderDiffTreeOptions): Promise<string> => {
  const renderChildren = async () =>
    (await Promise.all((node.children ?? []).map((child) => renderNode(child, options)))).join('');

  const renderDefault = async () => renderDefaultNode(node, options);
  const helpers: RenderSpecialNodeHelpers = {
    renderChildren,
    renderDefault,
    wrapInserted: (html) => wrapInsertedNode(html, node),
    wrapDeleted: (html) => wrapDeletedNode(html, node),
    wrapCurrentMarks: (html) => (node.isInline ? wrapMarksHtml(html, node.marks) : html),
    wrapPreviousMarks: (html) => (node.isInline ? wrapMarksHtml(html, node.prevMarks ?? node.marks) : html),
  };

  if (options.renderSpecialNode) {
    const specialHtml = await options.renderSpecialNode(node, helpers);
    if (specialHtml != null) return specialHtml;
  }

  return renderDefault();
};

export const renderDiffTree = async (root: TDiffNode, options: RenderDiffTreeOptions): Promise<string> =>
  renderNode(root, options);
